/**
 * Turning reminders on, and off.
 *
 * Reminders are the one part of this app that interrupts someone, so the way to turn them off
 * has to be as easy to find as the way to turn them on. Both live in Settings, one tap from
 * every screen. What is left out here is the invitation — a household with reminders off is
 * offered them where they are standing, because nobody goes hunting in Settings for a feature
 * they have not been told about.
 */

import { useEffect, useState } from 'preact/hooks';

import type { ApiClient } from '../api/client';
import {
  currentState,
  disableReminders,
  enableReminders,
  type ReminderState,
} from '../pwa/notifications';

interface Reminders {
  state: ReminderState | null;
  working: boolean;
  turnOn: () => void;
  turnOff: () => void;
}

/**
 * The last answer the browser gave, kept for the next component that asks.
 *
 * This is a fact about the device rather than about a screen, and the inventory screen asks for
 * it on load — so by the time Settings mounts it is nearly always already known. Starting from
 * `null` there anyway meant the Reminders section drew as a bare heading and then grew a row,
 * pushing everything below it down a frame or two after the screen appeared.
 */
let lastKnown: ReminderState | null = null;

/**
 * The reminder state of this device, and the two things that can be done about it.
 *
 * `state` is `null` only until the browser has been asked for the first time in this session,
 * which is a moment rather than a condition — callers render nothing until it resolves.
 */
export function useReminders(client: ApiClient): Reminders {
  const [state, setState] = useState<ReminderState | null>(lastKnown);
  const [working, setWorking] = useState(false);

  const remember = (next: ReminderState): void => {
    lastKnown = next;
    setState(next);
  };

  useEffect(() => {
    void currentState().then(remember);
  }, []);

  const change = (next: Promise<ReminderState>): void => {
    setWorking(true);
    void next
      .then(remember)
      // A failure here leaves the browser and the server possibly disagreeing, so the honest
      // thing is to go and ask rather than assume it worked.
      .catch(() => void currentState().then(remember))
      .finally(() => setWorking(false));
  };

  return {
    state,
    working,
    turnOn: () => change(enableReminders(client)),
    turnOff: () => change(disableReminders(client)),
  };
}

/** Offers reminders to a household that has not turned them on, and nothing otherwise. */
export function RemindersInvitation({ client }: { client: ApiClient }) {
  const reminders = useReminders(client);

  if (reminders.state !== 'off') {
    return null;
  }

  return (
    <aside class="reminders">
      <span>Get told when something needs using</span>
      <button type="button" disabled={reminders.working} onClick={reminders.turnOn}>
        Turn on
      </button>
    </aside>
  );
}
